import React, { useEffect, useRef, useState } from 'react';
import { Player } from '../../game';
import { formatMoney } from '../../game/presentation';

interface ScoreboardProps {
  players: Player[];
  currentPlayerId?: string;
  youId: string;
  gameEnded?: boolean;
}

const Scoreboard: React.FC<ScoreboardProps> = ({ players, currentPlayerId, youId, gameEnded }) => {
  const prev = useRef<Record<string, number>>({});
  const [pulse, setPulse] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const nextPulse: Record<string, boolean> = {};
    players.forEach((player) => {
      const before = prev.current[player.id];
      if (before != null && before !== player.totalScore + player.roundScore) {
        nextPulse[player.id] = true;
      }
      prev.current[player.id] = player.totalScore + player.roundScore;
    });
    if (Object.keys(nextPulse).length) {
      setPulse(nextPulse);
      const timer = window.setTimeout(() => setPulse({}), 560);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [players]);

  return (
    <div
      className="ss-scores"
      data-count={Math.min(players.length, 4)}
      role="list"
      aria-label="Scoreboard"
    >
      {players.map((player) => {
        const isTurn = !gameEnded && player.id === currentPlayerId;
        const isYou = player.id === youId;
        return (
          <article
            key={player.id}
            className={`ss-player${isTurn ? ' is-turn' : ''}${isYou ? ' is-you' : ''}${
              pulse[player.id] ? ' is-pulse' : ''
            }`}
            role="listitem"
            aria-current={isTurn ? 'true' : undefined}
          >
            <div className="ss-player-name">
              <span>{player.name}</span>
              {isYou && <span className="ss-you">You</span>}
              {isTurn && <span className="ss-turn-pill">Turn</span>}
            </div>
            <div className="ss-money">
              <span className="bank" aria-label={`${player.name} banked ${formatMoney(player.totalScore)}`}>
                {formatMoney(player.totalScore)}
              </span>
              <span
                className={`round${player.roundScore > 0 ? ' is-hot' : ''}`}
                aria-label={`${player.name} this round ${formatMoney(player.roundScore)}`}
              >
                {player.roundScore > 0 ? `+${formatMoney(player.roundScore)}` : 'round $0'}
              </span>
            </div>
          </article>
        );
      })}
    </div>
  );
};

export default Scoreboard;
